import { Button, Popconfirm, Space, Table, TablePaginationConfig } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import routes from '@/constants/routes'
import { useAuth } from '@/hooks/useAuth'
import { Employee } from '@/types'

import { CustomRowTable } from './styles'

type Props = {
  dataSource: Employee[] | undefined
  getEmployeesLoading: boolean
  page: number
  pageSize: number
  totalItems: number
  deleteEmployeeLoading: boolean
  onChange: (config: TablePaginationConfig) => void
  onDelete: (id: Employee['id']) => void
}

const EmployeesTable = ({
  dataSource,
  getEmployeesLoading,
  deleteEmployeeLoading,
  page,
  pageSize,
  totalItems,
  onChange,
  onDelete,
}: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { hasPermission } = useAuth()
  const [visibleConfirmDelete, setVisibleConfirmDelete] = useState<Record<string, boolean>>({})

  const columns: ColumnsType<Employee> = [
    {
      title: t('components.pages.employees.list.table.id'),
      dataIndex: 'id',
      key: 'id',
      render: (text: string, record: Employee) => {
        return <CustomRowTable type="right">{record.id}</CustomRowTable>
      },
      align: 'center' as const,
    },
    {
      title: t('components.pages.employees.list.table.code'),
      dataIndex: 'tagCode',
      key: 'tagCode',
      render: (text: string, record: Employee) => {
        return <CustomRowTable type="right">{record.tagCode}</CustomRowTable>
      },
      align: 'center' as const,
    },
    {
      title: t('components.pages.employees.list.table.lastName'),
      dataIndex: 'lastName',
      key: 'lastName',
      render: (text: string) => {
        return <CustomRowTable type="left">{text}</CustomRowTable>
      },
      align: 'center' as const,
    },
    {
      title: t('components.pages.employees.list.table.firstName'),
      dataIndex: 'firstName',
      key: 'firstName',
      render: (text: string) => {
        return <CustomRowTable type="left">{text}</CustomRowTable>
      },
      align: 'center' as const,
    },
    {
      title: t('components.pages.employees.list.table.gender'),
      dataIndex: 'gender',
      key: 'gender',
      render: (text: string) => {
        return <CustomRowTable type="left">{text}</CustomRowTable>
      },
      align: 'center' as const,
    },
    {
      title: t('components.pages.employees.list.table.position'),
      dataIndex: 'position',
      key: 'position',
      render: (text: string, record: Employee) => {
        return <CustomRowTable type="left">{record.position}</CustomRowTable>
      },
      align: 'center' as const,
    },
    {
      title: t('components.pages.employees.list.table.workingForm'),
      dataIndex: 'shiftType',
      key: 'shiftType',
      render: (text: string, record: Employee) => {
        return <CustomRowTable type="left">{record.shiftType}</CustomRowTable>
      },
      align: 'center' as const,
    },
    {
      title: t('components.pages.employees.list.table.workingHours'),
      dataIndex: 'contractHours',
      key: 'contractHours',
      render: (text: string, record: Employee) => {
        return <CustomRowTable type="right">{record.contractHours.toFixed(1)}</CustomRowTable>
      },
      align: 'center' as const,
    },

    {
      title: t('components.pages.employees.list.table.department'),
      dataIndex: 'department',
      key: 'department',
      render: (text: string, record: Employee) => {
        return <CustomRowTable type="right">{record.department}</CustomRowTable>
      },
      align: 'center' as const,
    },
  ]

  if (hasPermission('employee/update')) {
    columns.push({
      align: 'center' as const,
      render: (text: string, record: Employee) => {
        return (
          <Space size="middle">
            <Button size="small" onClick={() => goToEditEmployeePage(record.id)}>
              {t('components.pages.admins.list.table.update')}
            </Button>
            <Popconfirm
              id={record.id.toString()}
              visible={!!visibleConfirmDelete[record.id]}
              title={t('components.pages.attendanceLogs.list.table.confirmDelete')}
              okText={t('common.yes')}
              cancelText={t('common.no')}
              onConfirm={() => onDelete(record.id)}
              onCancel={() => setVisibleConfirmDelete({})}
            >
              <Button
                size="small"
                danger
                onClick={() => setVisibleConfirmDelete({ [record.id]: true })}
                loading={deleteEmployeeLoading}
              >
                {t('components.pages.admins.list.table.delete')}
              </Button>
            </Popconfirm>
          </Space>
        )
      },
    })
  }

  const goToEditEmployeePage = (id: Employee['id']) => {
    navigate(`${routes.employees.path}/${id}`)
  }

  return (
    <Table
      rowKey="id"
      bordered
      dataSource={dataSource}
      columns={columns}
      loading={getEmployeesLoading}
      pagination={{
        pageSize,
        current: page,
        total: totalItems,
        showSizeChanger: false,
      }}
      onChange={onChange}
    />
  )
}

EmployeesTable.displayName = 'EmployeesTable'
export default EmployeesTable
